import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import studentIcon from '../assets/welcome-student.png';
import teacherIcon from '../assets/welcome-teacher.png';
import './styles/WelcomeBuddy.css';

const VkLogo = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.785 16.241s.288-.032.436-.194c.136-.148.132-.427.132-.427s-.02-1.304.587-1.496c.598-.19 1.367 1.26 2.182 1.818.616.422 1.084.33 1.084.33l2.177-.03s1.138-.071.598-.964c-.044-.073-.314-.661-1.618-1.869-1.365-1.263-1.182-1.058.462-3.241.999-1.328 1.399-2.14 1.273-2.486-.12-.33-.859-.243-.859-.243l-2.451.015s-.182-.025-.316.056c-.13.079-.214.263-.214.263s-.385 1.023-.897 1.893c-1.08 1.834-1.513 1.931-1.69 1.818-.412-.264-.309-1.06-.309-1.626 0-1.768.268-2.503-.522-2.695-.263-.064-.456-.106-1.127-.113-.861-.009-1.59.003-2.003.206-.275.135-.487.435-.358.452.16.021.522.098.714.38.247.364.238 1.182.238 1.182s.142 2.25-.33 2.529c-.324.191-.769-.199-1.725-1.845-.489-.84-.859-1.77-.859-1.77s-.071-.174-.198-.267c-.154-.113-.37-.149-.37-.149l-2.328.015s-.35.01-.478.162c-.114.135-.009.414-.009.414s1.805 4.219 3.847 6.342c1.872 1.947 3.995 1.819 3.995 1.819h.963z" />
  </svg>
);

const PHRASES = [
  'Привет!',
  'Выбери карточку',
  'Студент или преподаватель?',
  'Откроем расписание?',
];

const DRAG_PHRASES = [
  'Что ты делаешь?!',
  'Эй, куда?!',
  'Отпусти 😅',
  'Зачем тянешь?',
  'Ого...',
  'Стоп-стоп!',
];

const TAP_PHRASES = [
  'Хватит тыкать!',
  'Эй, не тыкай)',
  'Хватит тыкать 😤',
  'Ай!',
  'Перестань тыкать!',
];

const SPRING = 0.16;
const FRICTION = 0.82;
const MAX_DRAG = 108;
const TAP_MOVE = 12;
const LOOK_X = 14;
const LOOK_Y = 12;
const IDLE_SLEEP_MS = 10000;
const SLEEP_YAWN_MS = 2100;
const TAP_BURST_MS = 2600;

const makeTapBurst = () => {
  const count = 22 + Math.floor(Math.random() * 8);
  const particles = Array.from({ length: count }, (_, i) => {
    const angle =
      (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.85;
    const dist = 64 + Math.random() * 110;
    const lift = 36 + Math.random() * 72;
    const drift = (Math.random() - 0.5) * 36;
    return {
      id: `${i}-${Math.random().toString(36).slice(2, 6)}`,
      x: Math.cos(angle) * dist + drift,
      y: Math.sin(angle) * dist * 0.72 - lift,
      mx: Math.cos(angle) * dist * 0.45 + drift * 0.4,
      my: Math.sin(angle) * dist * 0.28 - lift * 0.45,
      size: 3.5 + Math.random() * 8,
      delay: Math.random() * 0.22,
      dur: 1.7 + Math.random() * 0.75,
      spin: (Math.random() - 0.5) * 260,
      tone: Math.random() > 0.55 ? 'a' : Math.random() > 0.4 ? 'b' : 'c',
    };
  });
  return { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, particles };
};

const WelcomeSelect = ({
  value,
  onChange,
  options = [],
  placeholder = '',
  disabled = false,
  label = '',
  icon = 'bi-search',
}) => {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(true);
  const rootRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const filtered = options.filter((opt) =>
    String(opt).toLowerCase().includes(String(query || '').toLowerCase()),
  );

  const pick = (opt) => {
    onChange(opt);
    setQuery(opt);
    setOpen(false);
  };

  return (
    <div className="welcome-buddy__select" ref={rootRef}>
      {label ? (
        <label className="welcome-buddy__select-label">
          <i className={`bi ${icon}`} aria-hidden="true" />
          {label}
        </label>
      ) : null}
      <div className="welcome-buddy__select-wrap">
        <input
          type="text"
          className="welcome-buddy__select-input"
          value={query}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (value) onChange('');
          }}
          onFocus={() => setOpen(true)}
          autoComplete="off"
        />
        <button
          type="button"
          className="welcome-buddy__select-toggle"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          aria-label="Открыть список"
        >
          <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} aria-hidden="true" />
        </button>
        {open && !disabled ? (
          <div className="welcome-buddy__select-menu" role="listbox">
            {filtered.length === 0 ? (
              <div className="welcome-buddy__select-empty">Ничего не найдено</div>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  role="option"
                  aria-selected={opt === value}
                  className={`welcome-buddy__select-option${opt === value ? ' is-selected' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(opt);
                  }}
                >
                  <span>{opt}</span>
                  {opt === value ? <i className="bi bi-check-lg" aria-hidden="true" /> : null}
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Приветственный круг + карточки выбора, затем анимированный выбор группы/преподавателя.
 */
const WelcomeBuddy = ({
  groups = [],
  teachers = [],
  loadingData = false,
  onComplete,
  settingsOpen = false,
  showVkLink = false,
}) => {
  const navigate = useNavigate();
  const buddyRootRef = useRef(null);
  const buddyRef = useRef(null);
  const faceRef = useRef(null);
  const dragRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    moved: 0,
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    raf: 0,
    dragSaid: false,
  });
  const tapTimerRef = useRef(0);
  const idleTimerRef = useRef(0);
  const drowsyTimerRef = useRef(0);
  const phraseIndexRef = useRef(0);
  const dragPhraseRef = useRef(0);
  const tapPhraseRef = useRef(0);
  const leavingRef = useRef(false);
  const sleepingRef = useRef(false);
  const drowsyRef = useRef(false);
  const [phrase, setPhrase] = useState(PHRASES[0]);
  const [phraseKey, setPhraseKey] = useState(0);
  const [surprised, setSurprised] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [drowsy, setDrowsy] = useState(false);
  const [sleeping, setSleeping] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [gone, setGone] = useState(false);
  const [phase, setPhase] = useState('choice'); // choice | select
  const [pickType, setPickType] = useState('');
  const [cardsLeaving, setCardsLeaving] = useState(false);
  const [selectValue, setSelectValue] = useState('');
  const [tapBursts, setTapBursts] = useState([]);

  leavingRef.current = leaving;
  sleepingRef.current = sleeping;
  drowsyRef.current = drowsy;

  const bumpPhrase = useCallback(() => {
    phraseIndexRef.current = (phraseIndexRef.current + 1) % PHRASES.length;
    setPhrase(PHRASES[phraseIndexRef.current]);
    setPhraseKey((k) => k + 1);
  }, []);

  const sayDragPhrase = useCallback(() => {
    dragPhraseRef.current = (dragPhraseRef.current + 1) % DRAG_PHRASES.length;
    setPhrase(DRAG_PHRASES[dragPhraseRef.current]);
    setPhraseKey((k) => k + 1);
  }, []);

  const sayTapPhrase = useCallback(() => {
    tapPhraseRef.current = (tapPhraseRef.current + 1) % TAP_PHRASES.length;
    setPhrase(TAP_PHRASES[tapPhraseRef.current]);
    setPhraseKey((k) => k + 1);
  }, []);

  const clearSleepTimers = useCallback(() => {
    if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    if (drowsyTimerRef.current) window.clearTimeout(drowsyTimerRef.current);
    idleTimerRef.current = 0;
    drowsyTimerRef.current = 0;
  }, []);

  const armIdleSleep = useCallback(() => {
    clearSleepTimers();
    if (leavingRef.current) return;
    idleTimerRef.current = window.setTimeout(() => {
      if (leavingRef.current || dragRef.current.active) return;
      setSurprised(false);
      setDrowsy(true);
      setPhrase('Ааа…');
      setPhraseKey((k) => k + 1);
      drowsyTimerRef.current = window.setTimeout(() => {
        if (leavingRef.current || dragRef.current.active) return;
        setSleeping(true);
        setDrowsy(false);
        setPhrase('Zzz…');
        setPhraseKey((k) => k + 1);
      }, SLEEP_YAWN_MS);
    }, IDLE_SLEEP_MS);
  }, [clearSleepTimers]);

  const wakeUp = useCallback(
    (opts = {}) => {
      const wasAsleep = sleepingRef.current || drowsyRef.current;
      clearSleepTimers();
      setDrowsy(false);
      if (sleepingRef.current) setSleeping(false);
      if (wasAsleep && opts.sayHello !== false) {
        setPhrase(opts.phrase || 'Ой, я тут!');
        setPhraseKey((k) => k + 1);
      }
      if (!leavingRef.current) armIdleSleep();
    },
    [armIdleSleep, clearSleepTimers],
  );

  const jellyRadius = (x, y) => {
    const nx = Math.max(-1, Math.min(1, x / MAX_DRAG));
    const ny = Math.max(-1, Math.min(1, y / MAX_DRAG));
    const pull = Math.min(1, Math.hypot(nx, ny));
    const tl = 50 + nx * 32 - ny * 26;
    const tr = 50 - nx * 32 - ny * 26;
    const br = 50 - nx * 32 + ny * 26;
    const bl = 50 + nx * 32 + ny * 26;
    const soft = 42 + pull * 22;
    return `${tl}% ${tr}% ${br}% ${bl}% / ${soft}% ${soft}% ${100 - soft}% ${100 - soft}%`;
  };

  const setLookVars = (x, y) => {
    const root = buddyRootRef.current;
    if (!root) return;
    const lx = Math.max(-1, Math.min(1, x / MAX_DRAG)) * LOOK_X;
    const ly = Math.max(-1, Math.min(1, y / MAX_DRAG)) * LOOK_Y;
    root.style.setProperty('--look-x', String(lx));
    root.style.setProperty('--look-y', String(ly));
  };

  const clearLookVars = () => {
    const root = buddyRootRef.current;
    if (!root) return;
    root.style.setProperty('--look-x', '0');
    root.style.setProperty('--look-y', '0');
  };

  const applyPose = useCallback(() => {
    const state = dragRef.current;
    const el = buddyRef.current;
    const face = faceRef.current;
    if (!el) return;
    const nx = Math.max(-1, Math.min(1, state.x / MAX_DRAG));
    const ny = Math.max(-1, Math.min(1, state.y / MAX_DRAG));
    const pull = Math.min(1, Math.hypot(nx, ny));
    const squashX = 1 + Math.abs(nx) * 0.14 - Math.abs(ny) * 0.08;
    const squashY = 1 + Math.abs(ny) * 0.16 - Math.abs(nx) * 0.08;
    const skew = nx * -7;
    el.style.transform = `translate3d(${state.x}px, ${state.y}px, 0)`;
    if (face) {
      face.style.borderRadius = jellyRadius(state.x, state.y);
      face.style.transform = `scale(${squashX}, ${squashY}) skewX(${skew}deg)`;
      face.style.filter = `blur(${pull * 0.15}px)`;
    }
    setLookVars(state.x, state.y);
  }, []);

  const clearPose = useCallback(() => {
    const el = buddyRef.current;
    const face = faceRef.current;
    if (el) el.style.transform = '';
    if (face) {
      face.style.borderRadius = '';
      face.style.transform = '';
      face.style.filter = '';
    }
    clearLookVars();
  }, []);

  const playTap = useCallback(() => {
    const el = buddyRef.current;
    if (!el || leavingRef.current) return;
    const fromSleep = sleepingRef.current;
    wakeUp({ sayHello: false });
    setSurprised(true);
    setLookVars(0, -MAX_DRAG * 0.45);
    if (fromSleep) {
      setPhrase('Ой, я тут!');
      setPhraseKey((k) => k + 1);
    } else {
      sayTapPhrase();
    }
    const burst = makeTapBurst();
    setTapBursts((prev) => [...prev.slice(-2), burst]);
    window.setTimeout(() => {
      setTapBursts((prev) => prev.filter((b) => b.id !== burst.id));
    }, TAP_BURST_MS);
    el.classList.remove('is-tap');
    void el.offsetWidth;
    el.classList.add('is-tap');
    if (tapTimerRef.current) window.clearTimeout(tapTimerRef.current);
    tapTimerRef.current = window.setTimeout(() => {
      el.classList.remove('is-tap');
      setSurprised(false);
      clearLookVars();
      tapTimerRef.current = 0;
    }, 460);
  }, [sayTapPhrase, wakeUp]);

  useEffect(() => {
    const t = window.setInterval(() => {
      if (
        dragRef.current.active ||
        leavingRef.current ||
        sleepingRef.current ||
        drowsyRef.current ||
        surprised ||
        dragging ||
        phase !== 'choice'
      ) {
        return;
      }
      bumpPhrase();
    }, 3200);
    return () => window.clearInterval(t);
  }, [bumpPhrase, surprised, dragging, phase]);

  useEffect(() => {
    armIdleSleep();
    return () => clearSleepTimers();
  }, [armIdleSleep, clearSleepTimers]);

  useEffect(() => () => {
    if (tapTimerRef.current) window.clearTimeout(tapTimerRef.current);
    clearSleepTimers();
    if (dragRef.current.raf) window.cancelAnimationFrame(dragRef.current.raf);
  }, [clearSleepTimers]);

  const startSpring = useCallback(() => {
    const state = dragRef.current;
    const preferReduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (preferReduce || leavingRef.current || state.raf) {
      clearLookVars();
      return;
    }

    const tick = () => {
      if (leavingRef.current || state.active) {
        state.raf = 0;
        return;
      }
      state.vx += -state.x * SPRING;
      state.vy += -state.y * SPRING;
      state.vx *= FRICTION;
      state.vy *= FRICTION;
      state.x += state.vx;
      state.y += state.vy;
      if (Math.hypot(state.x, state.y) < 0.35 && Math.hypot(state.vx, state.vy) < 0.35) {
        state.x = 0;
        state.y = 0;
        state.vx = 0;
        state.vy = 0;
        clearPose();
        state.raf = 0;
        return;
      }
      applyPose();
      state.raf = window.requestAnimationFrame(tick);
    };

    state.raf = window.requestAnimationFrame(tick);
  }, [applyPose, clearPose]);

  const onPointerDown = (e) => {
    if (leavingRef.current) return;
    if (e.button != null && e.button !== 0) return;
    const el = buddyRef.current;
    if (!el) return;
    wakeUp({ sayHello: false });
    const state = dragRef.current;
    state.active = true;
    state.pointerId = e.pointerId;
    state.originX = e.clientX;
    state.originY = e.clientY;
    state.moved = 0;
    state.dragSaid = false;
    state.startX = e.clientX - state.x;
    state.startY = e.clientY - state.y;
    state.vx = 0;
    state.vy = 0;
    try {
      el.setPointerCapture(e.pointerId);
    } catch (_) {
      /* ignore */
    }
    el.classList.add('is-press');
    if (state.raf) {
      window.cancelAnimationFrame(state.raf);
      state.raf = 0;
    }
  };

  const onPointerMove = (e) => {
    const state = dragRef.current;
    if (!state.active || state.pointerId !== e.pointerId) return;
    state.moved = Math.max(
      state.moved,
      Math.hypot(e.clientX - state.originX, e.clientY - state.originY),
    );

    let nx = e.clientX - state.startX;
    let ny = e.clientY - state.startY;
    const dist = Math.hypot(nx, ny);
    if (dist > MAX_DRAG) {
      const k = MAX_DRAG / dist;
      nx *= k;
      ny *= k;
    }
    state.x = nx;
    state.y = ny;

    const preferReduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!preferReduce && state.moved > 10) {
      if (!state.dragSaid) {
        state.dragSaid = true;
        sayDragPhrase();
        setSurprised(true);
        setDragging(true);
        buddyRef.current?.classList.add('is-dragging');
        buddyRef.current?.classList.remove('is-press');
      }
      applyPose();
      return;
    }

    if (preferReduce) return;
    applyPose();
  };

  const endPointer = (e) => {
    const state = dragRef.current;
    if (!state.active) return;
    if (e && state.pointerId != null && e.pointerId !== state.pointerId) return;
    const wasTap = state.moved < TAP_MOVE;
    state.active = false;
    state.pointerId = null;
    state.dragSaid = false;
    buddyRef.current?.classList.remove('is-dragging', 'is-press');
    setDragging(false);
    if (wasTap) {
      state.x = 0;
      state.y = 0;
      state.vx = 0;
      state.vy = 0;
      clearPose();
      playTap();
      return;
    }
    setSurprised(false);
    buddyRef.current?.classList.add('is-wobble');
    window.setTimeout(() => buddyRef.current?.classList.remove('is-wobble'), 520);
    startSpring();
    armIdleSleep();
  };

  const finishOpen = useCallback((after) => {
    if (leaving || gone) return;
    const el = buddyRef.current;
    if (el) {
      el.style.transform = '';
      el.classList.remove('is-dragging', 'is-wobble', 'is-tap', 'is-press');
    }
    if (faceRef.current) {
      faceRef.current.style.borderRadius = '';
      faceRef.current.style.transform = '';
      faceRef.current.style.filter = '';
    }
    clearLookVars();
    clearSleepTimers();
    dragRef.current.active = false;
    dragRef.current.x = 0;
    dragRef.current.y = 0;
    dragRef.current.vx = 0;
    dragRef.current.vy = 0;
    if (dragRef.current.raf) {
      window.cancelAnimationFrame(dragRef.current.raf);
      dragRef.current.raf = 0;
    }
    setSurprised(false);
    setDragging(false);
    setDrowsy(false);
    setSleeping(false);
    setLeaving(true);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.setTimeout(() => {
      setGone(true);
      after?.();
    }, reduce ? 40 : 420);
  }, [clearSleepTimers, gone, leaving]);

  const chooseType = (type) => {
    if (cardsLeaving || phase !== 'choice') return;
    wakeUp({ sayHello: false });
    setPickType(type);
    setSelectValue('');
    setCardsLeaving(true);
    setPhrase(type === 'student' ? 'Выбери группу' : 'Выбери преподавателя');
    setPhraseKey((k) => k + 1);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.setTimeout(() => {
      setPhase('select');
      setCardsLeaving(false);
    }, reduce ? 40 : 320);
  };

  const backToChoice = () => {
    wakeUp({ sayHello: false });
    setPhase('choice');
    setPickType('');
    setSelectValue('');
    setPhrase(PHRASES[0]);
    setPhraseKey((k) => k + 1);
  };

  const handleSelect = (value) => {
    if (!value || !pickType) return;
    wakeUp({ sayHello: false });
    setSelectValue(value);
    const type = pickType;
    finishOpen(() => {
      onComplete?.({
        type,
        group: type === 'student' ? value : '',
        teacher: type === 'teacher' ? value : '',
      });
    });
  };

  useEffect(() => {
    if (!settingsOpen || leaving || gone) return;
    finishOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsOpen]);

  useEffect(() => {
    if (settingsOpen || !gone) return undefined;
    const t = window.setTimeout(() => {
      setGone(false);
      setLeaving(false);
      setPhase('choice');
      setPickType('');
      setSelectValue('');
    }, 220);
    return () => window.clearTimeout(t);
  }, [settingsOpen, gone]);

  if (gone) return null;

  const isStudent = pickType === 'student';

  return (
    <div
      ref={buddyRootRef}
      className={`welcome-buddy${leaving ? ' is-leaving' : ''}${surprised ? ' is-surprised' : ''}${dragging ? ' is-dragging' : ''}${drowsy ? ' is-yawning' : ''}${sleeping ? ' is-sleeping' : ''}${phase === 'select' || cardsLeaving || pickType ? ' is-selecting' : ''}`}
      style={{ '--look-x': 0, '--look-y': 0 }}
      onPointerDownCapture={() => wakeUp({ sayHello: false })}
    >
      <div className="welcome-buddy__stage">
        <span className="welcome-buddy__bursts" aria-hidden="true">
          {tapBursts.map((burst) => (
            <span key={burst.id} className="welcome-buddy__burst">
              {burst.particles.map((p) => (
                <i
                  key={p.id}
                  className={`welcome-buddy__spark welcome-buddy__spark--${p.tone}`}
                  style={{
                    '--sx': `${p.x}px`,
                    '--sy': `${p.y}px`,
                    '--mx': `${p.mx}px`,
                    '--my': `${p.my}px`,
                    '--ss': `${p.size}px`,
                    '--sd': `${p.delay}s`,
                    '--sdur': `${p.dur}s`,
                    '--sr': `${p.spin}deg`,
                  }}
                />
              ))}
            </span>
          ))}
        </span>
        <span className="welcome-buddy__halo" aria-hidden="true" />
        <span className="welcome-buddy__zzz" aria-hidden="true">
          <i>z</i>
          <i>z</i>
          <i>Z</i>
        </span>

        <button
          type="button"
          className={`welcome-buddy__blob${sleeping ? ' is-sleeping' : ''}`}
          ref={buddyRef}
          aria-label={`${phrase}. Тапни или потяни`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endPointer}
          onPointerCancel={endPointer}
          onKeyDown={(e) => {
            if (e.key === ' ') {
              e.preventDefault();
              playTap();
            }
          }}
        >
          <span className="welcome-buddy__face" ref={faceRef} aria-hidden="true">
            <span className="welcome-buddy__eye welcome-buddy__eye--left" />
            <span className="welcome-buddy__eye welcome-buddy__eye--right" />
            <span className="welcome-buddy__brow welcome-buddy__brow--left" />
            <span className="welcome-buddy__brow welcome-buddy__brow--right" />
            <svg className="welcome-buddy__mouth welcome-buddy__mouth--smile" viewBox="0 0 40 18" fill="none">
              <path
                d="M8 4C12 12 28 12 32 4"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
              />
            </svg>
            <span className="welcome-buddy__mouth welcome-buddy__mouth--oh" aria-hidden="true" />
            <span className="welcome-buddy__mouth welcome-buddy__mouth--sleep" aria-hidden="true" />
          </span>
        </button>
      </div>

      <div className="welcome-buddy__copy" aria-live="polite">
        <h2 className="welcome-buddy__hello is-say" key={phraseKey}>
          {phrase}
        </h2>
      </div>

      <div className="welcome-buddy__actions">
        {phase === 'choice' || cardsLeaving ? (
          <div className={`welcome-buddy__cards${cardsLeaving ? ' is-leaving' : ''}${showVkLink ? ' has-vk' : ''}`}>
            <button
              type="button"
              className="welcome-buddy__card welcome-buddy__card--student"
              disabled={cardsLeaving || loadingData}
              onClick={() => chooseType('student')}
            >
              <img src={studentIcon} alt="" className="welcome-buddy__card-icon" draggable="false" />
              <span className="welcome-buddy__card-text">Расписание студенту</span>
            </button>
            <button
              type="button"
              className="welcome-buddy__card welcome-buddy__card--teacher"
              disabled={cardsLeaving || loadingData}
              onClick={() => chooseType('teacher')}
            >
              <img src={teacherIcon} alt="" className="welcome-buddy__card-icon" draggable="false" />
              <span className="welcome-buddy__card-text">Расписание преподавателю</span>
            </button>
            {showVkLink ? (
              <button
                type="button"
                className="welcome-buddy__vk"
                disabled={cardsLeaving}
                onClick={() => navigate('/profile/vk')}
              >
                <span className="welcome-buddy__vk-logo" aria-hidden="true">
                  <VkLogo />
                </span>
                <span className="welcome-buddy__vk-copy">
                  <strong>Привяжите ВКонтакте</strong>
                  <em>Расписание и сброс пароля в боте</em>
                </span>
                <i className="bi bi-chevron-right" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        ) : null}

        {phase === 'select' && !cardsLeaving ? (
          <div className="welcome-buddy__picker is-in" key={pickType}>
            <div className="welcome-buddy__picker-row">
              <button
                type="button"
                className="welcome-buddy__back"
                onClick={backToChoice}
                aria-label="Назад"
              >
                <i className="bi bi-chevron-left" aria-hidden="true" />
              </button>
              <WelcomeSelect
                value={selectValue}
                onChange={handleSelect}
                options={isStudent ? groups : teachers}
                placeholder={isStudent ? 'Начните вводить группу...' : 'Начните вводить ФИО...'}
                disabled={loadingData}
              />
            </div>
            {loadingData ? (
              <p className="welcome-buddy__hint">Загружаем список…</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default WelcomeBuddy;
